import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Stethoscope, Check, X, Ban, Plus, Calendar, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_TONE = {
  pending: "text-amber-700 border-amber-200 bg-amber-50",
  approved: "text-emerald-700 border-emerald-200 bg-emerald-50",
  rejected: "text-red-700 border-red-200 bg-red-50",
  cancelled: "text-muted-foreground border-border bg-muted/40",
};

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function SickLeavePage() {
  const { user } = useAuth();
  const canApprove = ["super_admin", "admin", "hr_user", "manager"].includes(user?.role);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const t = params.get("tab");
    return ["pending", "approved", "rejected", "cancelled", "all"].includes(t) ? t : "pending";
  });
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (tab === "pending") next.delete("tab"); else next.set("tab", tab);
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [tab]);
  const [entries, setEntries] = useState([]);
  const [balances, setBalances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());

  // Create dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", start_date: todayStr(), end_date: todayStr(), days: 1, type: "statutory", reason: "", medical_cert_received: false, certificate_note: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ents, bals, emps] = await Promise.all([
        api.get("/sick-leave"),
        api.get("/sick-leave/balances", { params: { year } }),
        api.get("/employees", { params: { status: "active" } }),
      ]);
      setEntries(ents.data);
      setBalances(bals.data);
      setEmployees(emps.data);
      if (!form.employee_id && emps.data[0]) setForm((f) => ({ ...f, employee_id: emps.data[0].id }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const filtered = useMemo(() => {
    return entries.filter((e) => tab === "all" ? true : e.status === tab);
  }, [entries, tab]);

  const decide = async (id, decision, note) => {
    try {
      await api.patch(`/sick-leave/${id}/decision`, { decision, note });
      toast.success(`Request ${decision === "approve" ? "approved" : decision === "reject" ? "rejected" : "cancelled"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    if (!form.employee_id) return toast.error("Pick an employee");
    if (form.end_date < form.start_date) return toast.error("End date before start date");
    setSaving(true);
    try {
      const payload = {
        ...form, days: Number(form.days),
        reason: form.reason || undefined, certificate_note: form.certificate_note || undefined,
      };
      await api.post("/sick-leave", payload);
      toast.success("Sick leave submitted");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const pendingCount = entries.filter((e) => e.status === "pending").length;

  return (
    <div className="space-y-6" data-testid="sick-leave-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Wellness</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Sick leave</h1>
          <p className="text-muted-foreground mt-1">Irish statutory: 5 days @ 70% pay, €110/day cap (configurable in Settings → Leave).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="sick-new-btn"><Plus className="w-4 h-4 mr-2" /> Record sick leave</Button>
          </DialogTrigger>
          <DialogContent data-testid="sick-new-dialog">
            <DialogHeader>
              <DialogTitle>New sick-leave entry</DialogTitle>
              <DialogDescription>HR-created entries default to pending (same as employee self-service) — approve below.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Employee</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger data-testid="sick-emp"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="sick-start" /></div>
                <div><Label>End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="sick-end" /></div>
                <div><Label>Days</Label><Input type="number" min="0.5" step="0.5" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} data-testid="sick-days" /></div>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="statutory">Statutory (paid 70%, €110/day cap)</SelectItem>
                    <SelectItem value="company_scheme">Company scheme</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. flu, migraine" data-testid="sick-reason" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="mcr" checked={form.medical_cert_received} onChange={(e) => setForm({ ...form, medical_cert_received: e.target.checked })} />
                <Label htmlFor="mcr" className="cursor-pointer">Medical certificate received</Label>
              </div>
              {form.medical_cert_received && (
                <Textarea rows={2} placeholder="Certificate note" value={form.certificate_note} onChange={(e) => setForm({ ...form, certificate_note: e.target.value })} />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={saving} data-testid="sick-submit">{saving ? "Saving…" : "Submit"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Balances */}
      <Card className="border-border shadow-none">
        <CardContent className="p-0">
          <div className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="tiny-label">Sick leave balances</div>
              <div className="font-display text-lg font-semibold">Year {year}</div>
            </div>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-32" data-testid="sick-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, -1, -2].map((d) => { const y = new Date().getFullYear() + d; return <SelectItem key={y} value={String(y)}>{y}</SelectItem>; })}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto app-scroll">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left tiny-label">
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3 text-right">Entitlement</th>
                  <th className="px-4 py-3 text-right">Paid</th>
                  <th className="px-4 py-3 text-right">Unpaid</th>
                  <th className="px-4 py-3 text-right">Taken</th>
                  <th className="px-4 py-3 text-right">Remaining</th>
                  <th className="px-4 py-3">Policy</th>
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={b.employee_id} className="border-t border-border" data-testid={`sick-bal-${b.employee_id}`}>
                    <td className="px-4 py-3 font-medium">{b.employee_name}</td>
                    <td className="px-4 py-3 text-right font-mono">{b.entitlement_days.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right font-mono">{b.paid_days.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right font-mono">{b.unpaid_days.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{b.taken_days.toFixed(1)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${b.remaining_days < 0 ? "text-red-700" : b.remaining_days < 1 ? "text-amber-700" : "text-emerald-700"}`}>{b.remaining_days.toFixed(1)}</td>
                    <td className="px-4 py-3 text-xs capitalize">{b.policy.replace("_", " ")}</td>
                  </tr>
                ))}
                {balances.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">No data.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Entries + approval queue */}
      <Card className="border-border shadow-none">
        <CardContent className="p-0">
          <Tabs value={tab} onValueChange={setTab}>
            <div className="p-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="tiny-label">Sick leave entries</div>
                <div className="font-display text-lg font-semibold">All requests & decisions</div>
              </div>
              <TabsList>
                <TabsTrigger value="pending" data-testid="sick-tab-pending">Pending {pendingCount > 0 && <Badge variant="outline" className="ml-2">{pendingCount}</Badge>}</TabsTrigger>
                <TabsTrigger value="approved" data-testid="sick-tab-approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected" data-testid="sick-tab-rejected">Rejected</TabsTrigger>
                <TabsTrigger value="cancelled">Cancelled</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value={tab}>
              <div className="overflow-x-auto app-scroll">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr className="text-left tiny-label">
                      <th className="px-4 py-3">Employee</th>
                      <th className="px-4 py-3">Dates</th>
                      <th className="px-4 py-3 text-right">Days</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Reason</th>
                      <th className="px-4 py-3">Cert</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>}
                    {!loading && filtered.map((e) => (
                      <tr key={e.id} className="border-t border-border" data-testid={`sick-row-${e.id}`}>
                        <td className="px-4 py-3 font-medium">{e.employee_name}</td>
                        <td className="px-4 py-3 text-xs font-mono">{e.start_date}{e.end_date !== e.start_date ? ` → ${e.end_date}` : ""}</td>
                        <td className="px-4 py-3 text-right font-mono">{Number(e.days).toFixed(1)}</td>
                        <td className="px-4 py-3 text-xs capitalize">{(e.type || "").replace("_", " ")}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground max-w-[200px] truncate">{e.reason || "—"}</td>
                        <td className="px-4 py-3">{e.medical_cert_received ? <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px]">Received</Badge> : <span className="text-muted-foreground text-xs">—</span>}</td>
                        <td className="px-4 py-3"><Badge variant="outline" className={STATUS_TONE[e.status]}>{e.status}</Badge></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end">
                            {canApprove && e.status === "pending" && (
                              <>
                                <Button size="sm" variant="outline" className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => decide(e.id, "approve")} data-testid={`sick-approve-${e.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                                <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-700 hover:bg-red-50" onClick={() => { const n = window.prompt("Rejection reason (optional):"); decide(e.id, "reject", n); }} data-testid={`sick-reject-${e.id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                              </>
                            )}
                            {e.status === "pending" && <Button size="sm" variant="ghost" className="h-8" onClick={() => decide(e.id, "cancel")} data-testid={`sick-cancel-${e.id}`}><Ban className="w-3.5 h-3.5 mr-1" /> Cancel</Button>}
                            {e.estimated_pay_eur != null && e.status === "approved" && (
                              <Badge variant="outline" className="bg-sky-50 border-sky-200 text-sky-700 text-[10px] self-center" title={e.estimated_pay_label}>€{e.estimated_pay_eur.toFixed(2)}</Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!loading && filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No entries.</td></tr>}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
